/**
 * Front-end configuration for the GenAI chat assistant.
 *
 * SHOW / HIDE THE FEATURE
 * ----------------------------------------------------------------------------
 * `ENABLED` below is the master switch for the browser. Setting it to false
 * removes the launcher button and the chat window entirely - no network calls
 * are made and nothing renders.
 *
 * There is a second, independent switch on the server:
 *   server/genAIFeature/config/nlqSettings.json -> "enabled"
 * The assistant only appears when BOTH are true. The server flag is the one
 * that actually protects the API; this one controls the UI.
 *
 * `ENABLED` can also be driven per environment without editing code, by
 * setting REACT_APP_GENAI_ASSISTANT=false in client/app/.env (any value other
 * than the string "false" leaves the feature on).
 */

const envFlag = process.env.REACT_APP_GENAI_ASSISTANT;

const chatAssistantConfig = {
  /** Master on/off switch for the UI. */
  ENABLED: envFlag === undefined ? true : String(envFlag) !== "false",

  /**
   * Display name of the assistant, used in the title bar, the tooltips and the
   * fallback greeting. The server can override it from nlqSettings.json ->
   * ui.assistantName, which is what GET /nlq/config returns.
   */
  ASSISTANT_NAME: "MatchIQ",

  /** Hide the assistant on these routes (lowercase, no leading slash). */
  HIDDEN_ON_PAGES: ["login", "changepassword"],

  /** Only show it to these roles. Empty array = show to every logged-in user. */
  VISIBLE_TO_ROLES: [],

  /** Launcher button position. The chat window itself opens bottom-left. */
  LAUNCHER: {
    bottom: 24,
    right: 24,
  },

  /** Chat window geometry, in pixels. */
  WINDOW: {
    bottom: 24,
    left: 24,
    normalWidth: 440,
    normalHeight: 560,
    maximizedWidth: 900,
    maximizedHeight: 760,
  },

  /** Brand colours - kept in step with the app header and ExportIcon. */
  COLORS: {
    primary: "#005EB8",
    primaryDark: "#00447E",
    accent: "#1976d2",
  },

  /** Rows shown in the in-chat grid before it scrolls. */
  TABLE_MAX_HEIGHT: 260,

  /** Chart height in the chat window, and in the click-to-expand dialog. */
  CHART_HEIGHT: 220,
  CHART_EXPANDED_HEIGHT: 460,

  /**
   * Show the "View query" panel under an answer.
   *
   * The server has the authoritative switch (nlqSettings.json ->
   * ui.showGeneratedSql); when that is false the SQL is not even sent, so the
   * panel cannot appear whatever this says. Set this to false to hide the panel
   * in the browser only - useful for a demo build while the API keeps returning
   * the statement for troubleshooting.
   */
  SHOW_SQL_PANEL: true,

  /**
   * Pinned questions. The server has the authoritative switch
   * (nlqSettings.json -> pins.enabled) and also decides the per-user ceiling;
   * this one only hides the pin controls in the browser.
   */
  PINS_ENABLED: true,

  /** Prefix for downloaded CSV and chart JPEG files. */
  CSV_FILENAME_PREFIX: "matchiq",

  /** Conversation turns kept in the browser and replayed to the server. */
  MAX_CLIENT_HISTORY_TURNS: 6,
};

export default chatAssistantConfig;
